import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Popover, Checkbox, FormControlLabel, Divider, TextField, Typography, Link } from "@mui/material";
import { GridColDef, GridEventListener } from "@mui/x-data-grid";
import { IosShare, FilterList } from "@mui/icons-material";
import axios from "axios";
import { Order } from "@/interfaces";
import { OrderStatus } from "@/enums";
import { baseUrl, orderStatuses } from "@/consts";
import { formatCurrency, formatName, isNumber } from "@/utils";
import { MuiHelper, OrderHelper } from "@/helpers";
import { colors, sxButton, sxFont_12_500 } from "@/theme";
import { DebounceInput } from "@/components/atoms";
import { CrudGrid } from "@/components/organisms";
import { dateValueGetter } from "@/components/organisms/CrudGrid/helpers";

const orderStatusTag = (order?: Order) => (
  <Box
    sx={{
      ...sxFont_12_500,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "72px",
      height: "30px",
      borderRadius: "20px",
      color:
        order?.status === OrderStatus.PAID
          ? colors.mutedTealGreen
          : order?.status === OrderStatus.CANCELLED
            ? colors.white
            : colors.neutralGrey800,
      backgroundColor:
        order?.status === OrderStatus.PAID
          ? colors.lightGreen
          : order?.status === OrderStatus.CANCELLED
            ? colors.accent3Error
            : colors.neutralGrey300,
    }}
  >
    {orderStatuses[order?.status || 0]}
  </Box>
);

const orderStatusRender = (order?: Order) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      width: "100%",
    }}
  >
    {orderStatusTag(order)}
  </Box>
);

const columns: GridColDef[] = [
  {
    field: "orderId",
    headerName: "Order",
    width: 100,
    renderCell: (params) => (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <div>#{params.value}{params.row.offsite && <span style={{ color: "blue", marginLeft: 8, fontSize: 14 }}>OFF-SITE</span>} </div>
      </div>
    ),
  },
  {
    field: "dateTimeCreated",
    headerName: "Date",
    headerAlign: "center",
    align: "center",
    width: 100,
    type: "date",
    valueGetter: dateValueGetter,
  },
  {
    field: "lastName",
    headerName: "Customer name",
    headerAlign: "center",
    align: "center",
    width: 200,
    valueGetter: (_, row) => formatName(row.firstName, row.lastName),
  },
  {
    field: "dealerIdentifier",
    headerName: "Badge ID",
    headerAlign: "center",
    align: "center",
    width: 100,
  },
  {
    field: "masterDealerId",
    headerName: "Master ID",
    headerAlign: "center",
    align: "center",
    width: 100,
  },
  // === Money columns, grouped together in payment-math order ===
  // The order field (`Order.Total` in the DB) actually stores the *product
  // subtotal* — the sum of price × quantity before any discounts, fees, or
  // payment. The header used to say "Total cost" which was a misread of the
  // data. Renamed to "Subtotal" so the column matches what it represents.
  {
    field: "total",
    headerName: "Subtotal",
    width: 110,
    type: "number",
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: "discount",
    headerName: "Total Discounts",
    width: 130,
    type: "number",
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: "coopUsed",
    headerName: "Co-Op Applied",
    width: 130,
    type: "number",
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    // Renamed from "Coupon" → "Gift Card Applied" to match how the team
    // talks about this internally. The underlying field name (`couponUsed`)
    // is unchanged so no backend touchup needed — only the display label.
    field: "couponUsed",
    headerName: "Gift Card Applied",
    width: 140,
    type: "number",
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: "creditCardUsed",
    headerName: "Credit Card Applied",
    width: 150,
    type: "number",
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    // Order Total = the actual cost of the order: subtotal − co-op −
    // discounts + shipping. Deliberately EXCLUDES the gift card, which is a
    // payment method, not a price reduction. This column previously summed
    // credit card + gift card, so an $11 order paid with a $25 gift card
    // showed $25. Now it shows the real $11. Computed on the row via the same
    // OrderHelper.getOrderTotal used on the order detail page, so the list and
    // detail view always agree — no API/DB change needed.
    field: "orderTotal",
    headerName: "Order Total",
    width: 110,
    type: "number",
    valueGetter: (_, row) => OrderHelper.getOrderTotal(row),
    valueFormatter: (value) => formatCurrency(value),
    sortable: true,
  },
  // === Non-money / status columns ===
  {
    field: "shippingFee",
    headerName: "Shipping",
    width: 100,
    type: "number",
    valueFormatter: (value) => formatCurrency(value),
  },
  {
    field: "offsite",
    headerName: "Offsite",
    width: 100,
    type: "boolean",
  },
  {
    field: "status",
    headerName: "Status",
    headerAlign: "center",
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => orderStatusRender(params.row),
  },
];

const getRowId = (row: Order) => row.orderId;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const setRowId = (row: any, id: any) => {
  row.orderId = id;
};

// FiltersPopover component
function FiltersPopover() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null);
  const [status, setStatus] = useState({ paid: false, saved: false });
  const [shipping, setShipping] = useState<{ min: number | string; max: number | string }>({ min: 0, max: 0 });

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorEl(event.currentTarget);
    setAnchorPosition({
      top: rect.bottom,
      left: rect.left - 150, // shift 100px to the left
    });
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const handleStatusChange = (name: 'paid' | 'saved') => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setStatus({ paid: name === 'paid', saved: name === 'saved' });
    } else {
      setStatus({ paid: false, saved: false });
    }
  };
  const handleShippingChange = (name: 'min' | 'max') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/[^0-9]/g, '');
    let newValue = value === '' ? '' : Number(value);

    if (name === 'min') {
      let newMin = newValue;
      let newMax = shipping.max;
      if (newMin !== '' && newMax !== '' && Number(newMin) > Number(newMax)) {
        newMax = newMin;
      }
      setShipping({ min: newMin, max: newMax });
    } else {
      let newMax = newValue;
      let newMin = shipping.min;
      if (newMax !== '' && newMin !== '' && Number(newMax) < Number(newMin)) {
        newMin = newMax;
      }
      setShipping({ min: newMin, max: newMax });
    }
  };
  const handleResetPrice = () => {
    setShipping({ min: 1, max: 50 });
  };
  const handleClearAll = () => {
    setStatus({ paid: false, saved: false });
    setShipping({ min: 0, max: 0 });
  };
  const handleApply = () => {
    // Implement filter logic here
    handleClose();
  };

  return (
    <>
      <Button
        variant="text"
        sx={{
          color: '#2C3856',
          fontFamily: 'PF DIN Text Pro',
          fontSize: 16,
          textTransform: 'none',
          px: 1,
          py: 1,
          minWidth: 0,
          fontWeight: 400,
          gap: 1,
        }}
        startIcon={<FilterList sx={{ fontSize: 28, ml: 0.5 }} />}
        onClick={handleOpen}
      >
        Filters
      </Button>
      <Popover
        open={open}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition || { top: 0, left: 0 }}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 3,
            minWidth: 300,
            maxWidth: 340,
            p: 0,
          }
        }}
      >
        <Box sx={{ p: 3, pt: 2, pb: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 18, color: '#2C3856', mb: 1 }}>
            Filter by Status
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', pl:1, gap:2 }}>
            <FormControlLabel
              control={<Checkbox checked={status.paid} onChange={handleStatusChange('paid')} sx={{ p: 0 }} />}
              label={<Typography sx={{ fontSize: 16, color: '#2C3856', fontWeight: 500 }}>Paid</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={status.saved} onChange={handleStatusChange('saved')} sx={{ p: 0}} />}
              label={<Typography sx={{ fontSize: 16, color: '#2C3856', fontWeight: 500 }}>Saved</Typography>}
            />
          </Box>
        </Box>
        <Divider />
        <Box sx={{ p: 3, pt: 2, pb: 1 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 18, color: '#2C3856', mb: 1 }}>
            Filter by Shipping Fee
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <TextField
              value={shipping.min}
              onChange={handleShippingChange('min')}
              size="small"
              variant="outlined"
              sx={{ width: '40%', background: '#F7F8FA', borderRadius: 2 }}
              InputProps={{
                startAdornment: <Typography sx={{ color: '#A0AEC0', fontWeight: 600, fontSize: 16, mr: 0.5 }}>$</Typography>,
                sx: { fontWeight: 600, fontSize: 16, color: '#2C3856', pl: 0.5 }
              }}
            />
            <Typography sx={{ color: '#A0AEC0', fontWeight: 600, fontSize: 18 }}>—</Typography>
            <TextField
              value={shipping.max}
              onChange={handleShippingChange('max')}
              size="small"
              variant="outlined"
              sx={{ width: '40%', background: '#F7F8FA', borderRadius: 2 }}
              InputProps={{
                startAdornment: <Typography sx={{ color: '#A0AEC0', fontWeight: 600, fontSize: 16, mr: 0.5 }}>$</Typography>,
                sx: { fontWeight: 600, fontSize: 16, color: '#2C3856', pl: 0.5 }
              }}
            />
          </Box>
          <Box sx={{width:'100%',display:'flex', alignItems:'center', justifyContent:'center', mt:2}}>
            <Link component="button" underline="none" sx={{ color: '#1A7FF7', fontWeight: 500, fontSize: 16, cursor: 'pointer', mb: 1, alignSelf: 'center',fontFamily:'PF DIN Text Pro' }} onClick={handleResetPrice}>
              Reset Price Range
            </Link>
          </Box>
        </Box>
        <Divider />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3, pt: 2 }}>
          <Button variant="text" sx={{ color: '#2C3856', fontWeight: 500, fontSize: 16, textTransform: 'none' }} onClick={handleClearAll}>
            Clear all
          </Button>
          <Button variant="contained" sx={{ background: '#1A7FF7', color: '#fff', fontWeight: 600, fontSize: 16, borderRadius: 2, px: 4, boxShadow: 'none', textTransform: 'none', '&:hover': { background: '#1769c2' } }} onClick={handleApply}>
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
}

const Orders: React.FC = () => {
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();

  const getAllOrdersWithDealerEndPoint = "withdealer";

  const handleRowClick: GridEventListener<"rowClick"> = (params) => {
    if (!isNumber(params?.id as number)) {
      return;
    }

    navigate(`${params?.id}`);
  };

  const exportFn = async () => {
    try {
      setIsExporting(true);
      const now = new Date();
      const endDate = now.toISOString().replace('T', ' ').slice(0, 19);
      // Start from the beginning of the current year so the export only
      // includes this year's orders. Was previously hardcoded to
      // '2025-06-02 20:12:00' which is why old years were leaking in.
      // Computed dynamically so this keeps working in 2027, 2028, etc.
      const startDate = `${now.getUTCFullYear()}-01-01 00:00:00`;

      // Build the URL with URLSearchParams so we can conditionally add
      // `search`. The backend's /Orders/export endpoint accepts an optional
      // `search` query param (added 2026-05) that filters by OrderIdentifier,
      // dealer name/badge, product SKU, and product description — exactly
      // matching what the on-screen search box does for the list. We forward
      // whatever the user has typed so the CSV matches what they see.
      const params = new URLSearchParams({
        start: startDate,
        end: endDate,
        offsite: "false",
        custom: "true",
      });
      const trimmedSearch = search?.trim();
      if (trimmedSearch) {
        params.set("search", trimmedSearch);
      }
      const url = `${process.env.REACT_APP_REST_BASE_URL}/Orders/export?${params.toString()}`;
      console.log(url);

      // Use axios (not native fetch) so the global request interceptor in
      // src/utils/axiosConfig.ts attaches the JWT from localStorage. The old
      // fetch() call bypassed that and got a 401 because /Orders/export sits
      // behind the IpOrToken policy. responseType: 'blob' so the CSV bytes
      // are returned as a Blob (axios decodes JSON by default otherwise).
      const response = await axios.get<Blob>(url, {
        responseType: "blob",
        headers: { Accept: "text/csv" },
      });

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `orders_export_${new Date().toISOString().slice(0, 10)}.csv`; // filename with date
      document.body.appendChild(a);
      a.click();

      // Clean up
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);

      console.log('CSV file downloaded successfully');

    } catch (error) {
      console.error('Error exporting orders:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: 'center', justifyContent: 'space-between', p: 4 }}>
        {MuiHelper.getHeader("Orders")}
        {/* <Button sx={{ px: 3, py: 1.5, fontFamily: 'PF DIN Text Pro', backgroundColor: colors.primaryOzone500, color: colors.white, fontSize: 16, borderRadius: 2 }}>Add Order</Button> */}
      </Box>
      <Box sx={{ backgroundColor: colors.neutralGrey300, padding: 3, width:'auto' }}>
        <CrudGrid
          baseUrl={`${baseUrl.ORDER}/${getAllOrdersWithDealerEndPoint}`}
          columns={columns}
          getRowId={getRowId}
          setRowId={setRowId}
          fieldToFocus="orderIdentifier"
          initialState={{
            sorting: {
              sortModel: [{ field: "orderIdentifier", sort: "desc" }],
            },
          }}
          onRowClick={handleRowClick}
          search={search}
          // Capture search changes from the grid so exportFn can include the
          // current search term in its &search=... query param. Without this
          // the parent never sees what the user typed in the grid's own
          // search box, and the export ignored the filter.
          setSearch={setSearch}
          withoutActionColumn={true}
          exportButton={{
            text: 'Export',
            endIcon: <IosShare />,
            onClick: exportFn,
            isLoading: isExporting,
            sxButton: sxButton
          }}
          filtersButton={<FiltersPopover />}
        />
      </Box>
    </Box>
  );
};

export default Orders;
